import React, { useMemo, useState } from 'react'
import { Tooltip } from '../ui/modal'
import { SettingLineIcon } from './assets/setting-line-icon'
import { MaterialRangeSettingModal } from './material-range-setting-modal';
import { agentUserTemplateListHooks, getSceneByType, VIDEO_TEMPLATE_SCENE, VIDEO_TEMPLATE_TYPE } from '@/manager/manager/agent-user-template-list-manager';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

interface MaterialRangeSettingButtonProps {
  /** 视频模板类型 */
  scene?: VIDEO_TEMPLATE_TYPE
  /** 启用笔记素材 */
  enabledNote?: boolean;
}

/** 素材范围设置按钮 */
export const MaterialRangeSettingButton: React.FC<MaterialRangeSettingButtonProps> = ({
  scene,
  enabledNote
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // 只有口播体、混剪体才支持素材范围设置
  const enabledMaterial = [
    VIDEO_TEMPLATE_SCENE.BROADCAST,
    VIDEO_TEMPLATE_SCENE.MIXCUT
  ].includes(getSceneByType(scene) as VIDEO_TEMPLATE_SCENE);

  const {
    // 打开状态
    openUse,
    openPublic,
    openPrivate,
    openNote,
    setOpenUse,
    setOpenPublic,
    setOpenPrivate,
    setOpenNote,
    // 个人素材文件夹和标签配置
    isRelatedMaterial,
    hasTags,
    personalConfigs,
    toggleSelectTag,
    toggleSelectTagAll,
    filterPersonalConfigs,
    selectedTagCount,
    loading,
    updating,
    fetchTemplateMaterialSettings,
    updateMaterialSettings,
    handleChangeMaterialConfigData,
  } = agentUserTemplateListHooks.useTemplateMaterialSettings({
    scene,
    enabledMaterial,
    enabledNote
  });

  const tooltipText = useMemo(() => {
    // 基础判断：如果场景本身不支持素材设置
    if (!enabledMaterial) return t('template_material_range.not_use');

    // 当支持笔记素材时 (此分支下不考虑个人素材)
    if (enabledNote) {
      // 当前使用公共、笔记素材
      if (openPublic && openNote) return t('template_material_range.use_public_note');
      // 当前使用公共素材
      if (openPublic) return t('template_material_range.use_public');
      // 当前使用笔记素材
      if (openNote) return t('template_material_range.use_note');
      return t('template_material_range.not_use');
    }

    // 当不支持笔记素材时 (此分支下考虑个人素材)
    // 当前使用公共、个人素材
    if (openPublic && (openPrivate && isRelatedMaterial)) return t('template_material_range.use_public_private');
    // 当前使用公共素材
    if (openPublic) return t('template_material_range.use_public');
    // 当前使用个人素材
    if (openPrivate && isRelatedMaterial) return t('template_material_range.use_private');

    return t('template_material_range.not_use');
  }, [enabledMaterial, enabledNote, openPublic, openPrivate, openNote, isRelatedMaterial, t]);

  return (
    <>
      <Tooltip
        side='top'
        align='center'
        trigger={
          <div
            className={cn(
              "size-9 flex items-center justify-center rounded-[8px] hover:bg-[rgba(242,244,247,1)] transition-colors flex-shrink-0 cursor-pointer",
              !enabledMaterial && "opacity-50 cursor-not-allowed"
            )}
            onClick={() => {
              if (!enabledMaterial) return;
              handleChangeMaterialConfigData(scene)
              setOpen(!open)
            }}
          >
            <SettingLineIcon className="size-5" />
          </div>
        }
        content={
          !loading && <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]'>
            <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
              {tooltipText}
            </span>
            <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
          </div>
        }
      />
      <MaterialRangeSettingModal
        open={open}
        scene={scene}
        enabledMaterial={enabledMaterial}
        enabledNote={enabledNote}
        onOpenChange={setOpen}

        // 个人素材文件夹和标签配置
        // 打开状态
        openUse={openUse}
        openPublic={openPublic}
        openPrivate={openPrivate}
        openNote={openNote}
        setOpenUse={setOpenUse}
        setOpenPublic={setOpenPublic}
        setOpenPrivate={setOpenPrivate}
        setOpenNote={setOpenNote}
        // 个人素材文件夹和标签配置
        hasTags={hasTags}
        isRelatedMaterial={isRelatedMaterial}
        personalConfigs={personalConfigs}
        toggleSelectTag={toggleSelectTag}
        toggleSelectTagAll={toggleSelectTagAll}
        filterPersonalConfigs={filterPersonalConfigs}
        selectedTagCount={selectedTagCount}
        loading={loading}
        updating={updating}
        updateMaterialSettings={updateMaterialSettings}
      />
    </>
  )
}
